import React from "react";
import { Book, Key, AlertTriangle, Lock, Search, Shield, Smartphone } from "lucide-react";
import { Helmet } from "react-helmet";

const Navigation = () => {
  return (
    <nav className="dark-header">
      <a href="/">
        <img src="/logos/logo-v5.jpg" alt="ARCANA" className="dark-logo-nav" />
      </a>
      <div className="dark-nav">
        <a href="/" className="dark-nav-link">HOME</a>
        <a href="/#about" className="dark-nav-link">ABOUT</a>
        <a href="/services" className="dark-nav-link">SERVICES</a>
        <a href="/resources" className="dark-nav-link active">RESOURCES</a>
        <a href="/faq" className="dark-nav-link">FAQ</a>
        <button className="btn-primary" onClick={() => window.location.href = '/#assessment'}>
          FREE ASSESSMENT
        </button>
      </div>
      <button className="mobile-menu-btn">
        <span style={{color: 'var(--oro-arcana)'}}>☰</span>
      </button>
    </nav>
  );
};

const ResourcesPage = () => {
  const guides = [
    {
      icon: <Key size={48} />,
      title: "Seed Phrase Missing 1 Word",
      description: "Learn safe steps when one seed word is missing. What to do, what to avoid, and when recovery is feasible.",
      url: "/resources/seed-phrase-missing-1-word",
      category: "Recovery Guide"
    },
    {
      icon: <AlertTriangle size={48} />,
      title: "Seed Phrase Typos or Wrong Order",
      description: "Safe recovery approach for typos, language mismatches, and incorrect word ordering in BIP39 phrases.",
      url: "/resources/seed-phrase-typos-wrong-order",
      category: "Recovery Guide"
    },
    {
      icon: <Lock size={48} />,
      title: "Wallet Password Recovery (Owner-Authorised)",
      description: "Professional guide for recovering lost wallet.dat or keystore JSON passwords using controlled methods.",
      url: "/resources/wallet-password-recovery-owner-authorised",
      category: "Password Recovery"
    },
    {
      icon: <Search size={48} />,
      title: "Funds Not Showing? Derivation Paths Explained",
      description: "Wallet shows zero balance? Understand derivation paths, accounts, and safe checks to locate funds.",
      url: "/resources/funds-not-showing-derivation-paths",
      category: "Technical Guide"
    },
    {
      icon: <Shield size={48} />,
      title: "Compromised Wallet: First 30 Minutes Checklist",
      description: "Emergency response guide for suspected wallet compromise. Containment, safe migration, and evidence preservation.",
      url: "/resources/compromised-wallet-first-30-minutes",
      category: "Emergency Guide"
    },
    {
      icon: <Lock size={48} />,
      title: "BIP39 Passphrase (25th Word) Recovery",
      description: "Forgotten your BIP39 passphrase? Learn pattern analysis, dictionary methods, and when recovery is feasible.",
      url: "/resources/passphrase-25th-word-recovery",
      category: "Recovery Guide"
    },
    {
      icon: <Smartphone size={48} />,
      title: "Hardware Wallet Recovery (Ledger/Trezor)",
      description: "Seed phrase recovery for Ledger, Trezor, and all BIP39 hardware wallets. What we recover, what we cannot.",
      url: "/resources/hardware-wallet-ledger-trezor-recovery",
      category: "Hardware Guide"
    },
    {
      icon: <Shield size={48} />,
      title: "Inherited Wallet Recovery — Guide for Heirs, Executors & Solicitors",
      description: "Complete guide for recovering cryptocurrency from a deceased person's estate. Legal authority, recovery paths, scam red flags, and executor duties.",
      url: "/inherited-wallet-recovery-guide.html",
      category: "Estate / Probate"
    },
    {
      icon: <Search size={48} />,
      title: "4 or 5 Words Missing — Tier 3 No-Find-No-Fee Recovery",
      description: "When 4-5 BIP-39 words are missing, the search space reaches trillions. GPU farms, honest odds, and how our no-find-no-fee model works.",
      url: "/four-five-words-missing-tier3-recovery.html",
      category: "Tier 3 / GPU-Assisted"
    }
  ];

  return (
    <div className="App">
      <Helmet>
        <title>Crypto Recovery Resources & Guides | ARCANA CRYPTO</title>
        <meta name="description" content="Professional cryptocurrency recovery guides covering seed phrases, passwords, derivation paths, and emergency response. Free educational resources from UK-based experts." />
        <link rel="canonical" href="https://arcana-crypto.com/resources-seo.html" />
      </Helmet>

      <Navigation />
      
      <section className="section" style={{marginTop: '128px', minHeight: '100vh'}}>
        <div className="section-container">
          <h1 className="section-title">Recovery Resources & Technical Guides</h1>
          <p className="section-subtitle">
            Professional Educational Content on Cryptocurrency Recovery
          </p>

          <div className="section-intro" style={{marginTop: '40px'}}>
            <p style={{fontSize: '20px'}}>
              Evidence-based guides covering safe recovery practices, technical explanations, 
              and what to do (and avoid) when facing wallet access issues. All content is 
              provided for educational purposes and reflects our non-custodial, privacy-first approach.
            </p>
          </div>

          <div className="card-grid" style={{marginTop: '80px'}}>
            {guides.map((guide, index) => (
              <div key={index} className="card">
                <div className="card-icon">{guide.icon}</div>
                <div style={{
                  fontSize: '12px', 
                  color: 'var(--oro-arcana)', 
                  fontWeight: '600',
                  textTransform: 'uppercase',
                  letterSpacing: '1px',
                  marginBottom: '12px'
                }}>
                  {guide.category}
                </div>
                <h3 className="card-title">{guide.title}</h3>
                <p className="card-description">{guide.description}</p>
                <a href={guide.url} className="btn-secondary" style={{marginTop: '20px', width: '100%'}}>
                  READ GUIDE
                </a>
              </div>
            ))}
          </div>

          {/* Disclaimer */}
          <div style={{
            marginTop: '100px',
            background: 'var(--nero-grigio)',
            padding: '40px',
            border: '1px solid var(--oro-arcana)'
          }}>
            <h3 style={{color: 'var(--oro-arcana)', marginBottom: '20px', fontSize: '20px'}}>
              Important Notice
            </h3>
            <p style={{color: 'var(--grigio-chiaro)', lineHeight: '1.8', marginBottom: '16px'}}>
              These guides are educational resources only and do not constitute professional advice, 
              guarantees, or warranties. Cryptocurrency recovery is case-specific and probabilistic.
            </p>
            <p style={{color: 'var(--grigio-chiaro)', lineHeight: '1.8'}}>
              Arcana Crypto provides technical services on a non-custodial basis to verified rightful 
              owners within applicable legal boundaries. For case-specific assessment, contact us directly.
            </p>
          </div>

          {/* CTA */}
          <div style={{textAlign: 'center', marginTop: '80px'}}>
            <h2 className="section-title" style={{fontSize: '36px'}}>Need Professional Help?</h2>
            <p className="section-intro" style={{marginBottom: '40px'}}>
              Free confidential assessment for your specific case
            </p>
            <button className="btn-primary" style={{fontSize: '18px', padding: '18px 40px'}} onClick={() => window.location.href = '/#assessment'}>
              START FREE ASSESSMENT
            </button>
          </div>

        </div>
      </section>

      <footer className="footer">
        <div className="footer-content">
          <div className="footer-bottom">
            <p className="footer-copyright">
              © 2025-2026 ARCANA CRYPTO LTD - All Rights Reserved
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ResourcesPage;
